// Copyright (c) 2019-24 Stax·ai, Inc.
// See LICENSE.md file in project root directory

import React from 'react';
import { toast } from 'react-toastify';
import { withRouter } from 'react-router-dom';

import api from '../../api';

// Components
import User from '../User';
import Search from '../Search';
import FileViewer from '../FileViewer';

// Modals
import PublicPassword from '../Modals/Public/Password';
import PublicSignature from '../Modals/Public/Signature';

// Utilities
import download from '../../utilities/downloadable';
import print from '../../utilities/printable';
import rotatePage from '../../utilities/rotatePage';
import searchDocument from '../../utilities/searchDocument';

// Styles
import '../../styles/public.css';

class Public extends React.Component {
    state = {
        // Password-protected access
        access: {
            required: false,
            email: "",
            password: ""
        },

        // Document
        document: {
            _id: null,
            pages: [],
            words: []
        },

        // Search and matches
        search: '',
        matches: {
            count: 0,
            index: 0,
            words: []
        },
        searching: false,

        // Modal
        modal: "",
        modalData: {},

        iframe: true
    }

    componentDidMount = () => {
        const q = new URLSearchParams(this.props.location.search);
        this.setState({ iframe: q.get('iframe') === 'true' });
    }

    // Handle public/password check for API calls
    handleAccessCheck = async (call) => {
        const res = await call();
        if (res && res.requirePassword) {
            await this.setState({ access: {
                required: true,
                email: "",
                password: ""
            } });
            return null; // Signal to cancel
        }
        return res;
    }

    // Download document
    downloadDocument = async (doc) => {
        await this.props.loading(1005, "Preparing...");
        const res = await this.handleAccessCheck(async () => await api.download('/document/download?docId=' + doc._id));
        
        // File name cleanup
        let fileName;
        let fileNameCmps = doc.name.split(".");
        if (!doc.format || doc.format === "doc") {
            // Document/PDF file. Replace extension (if exists) with ".pdf"
            if (fileNameCmps.length > 1)
                fileNameCmps.pop();
            fileName = fileNameCmps.join(".") + ".pdf";
        }

        else // Format provided
            fileName = doc.name + "." + doc.format;

        if (res) download(res, fileName);
        this.props.loaded();
    }

    // Print document
    printDocument = async (doc) => {
        await this.props.loading(1005, "Preparing...");
        const res = await this.handleAccessCheck(async () => await api.download('/document/download?docId=' + doc._id));
        if (res) print(res, "application/pdf");
        this.props.loaded();
    }

    // Document search
    search = (search) => {
        const doc = this.state.document;
        if (this.searchTimeout)
            clearTimeout(this.searchTimeout);

        this.setState({ search, searching: true });

        // Immediate search if there aren't too many words
        if (doc.words && doc.words.length < 5000)
            return this.searchDocument(search, doc);

        // Else search after a timeout
        this.searchTimeout = setTimeout(() => this.searchDocument(search, doc), 500);
    }

    searchDocument = (search, doc) => {
        // Ensure document meta has loaded
        if (!doc.words || doc.words.length === 0)
            return setTimeout(() => this.searchDocument(search, this.state.document), 500); // Try again in half a second

        this.setState(searchDocument(search, doc));
    }

    submitFillable = async (metadata, signatures) => {
        await this.props.loading(1005, "Submitting...");
        const query = new URLSearchParams(this.props.location.search);
        const authEmail = this.state.access.email ? this.state.access.email : query.get('email');
        const from = query.get('from');

        const res = await api.post("/document/public/sign", {
            docId: this.state.document._id,
            authEmail,
            authPassword: this.state.access.password,
            metadata,
            signatures,
            from
        });
        if (res && res.success && res.documentId) {
            toast.success("You have completed and submitted this document! Great job!");

            // If this is an iframe use-case, then let the parent webpage know that the user has signed the document.
            if (this.state.iframe)
                window.top.postMessage({
                    action: "Document signed",
                    document: res.documentId
                }, "*");
        }
        this.props.loaded();
    }

    modal = (modal="", modalData={}) => this.setState({
        modal,
        modalData
    });

    render = () => {
        document.title = (this.state.document && this.state.document.name ? this.state.document.name + ' | ' : '') + 'Stax·ai - Put your workflow on autopilot';
        return (<>

            {/* Don't show logo, user icon and search bar in iframe mode */}
            {this.state.iframe ? <>
                {/* TODO: Minor branding */}
            </> : <>
                {/* Stax·ai logo */}
                <div className="public-logo" onClick={() => window.open('https://stax.ai')}>
                    <img
                        src="https://static.stax.ai/images/logo/color.svg"
                        alt="[Stax·ai Logo]"
                    />STAX<div className="navigation-logo-dot"></div>AI
                </div>

                {/* User icon */}
                <User
                    className="public-user"
                    clearNotification={this.props.clearNotification}
                    user={this.props.user}
                    updateUser={this.props.updateUser}
                    switchTeam={this.props.switchTeam}
                    team={this.props.team}
                />

                {/* Searchbar */}
                <Search
                    user={this.props.user}
                    className="public-search"
                    query={this.state.search}
                    activeZ={1005}
                    search={this.search}
                    searching={this.state.searching}
                />
            </>}
            
            {/* File Viewer */}
            <FileViewer
                public={true}
                user={this.props.user}
                team={this.props.team}
                loading={this.props.loading}
                loaded={this.props.loaded}
                document={this.state.document}
                format={this.state.document ? this.state.document.format : "doc"}
                setDocument={document => this.setState({ document, selection: [document._id] })}
                setWords={async (docId, words) => docId === this.state.document._id ? await this.setState({ document: {
                    ...this.state.document,
                    words
                } }) : {}}
                docs={[]}
                search={this.search}
                query={this.state.search}
                matches={this.state.matches}
                searching={this.state.searching}
                gotoMatch={index => this.setState({ matches: { ...this.state.matches, index } })}
                viewDoc={() => {}}
                modal={this.modal}

                // Operations
                downloadDocument={() => this.downloadDocument(this.state.document)}
                printDocument={() => this.printDocument(this.state.document)}
                rotatePage={(pageIdx, rotation, cloud=false, ignoreCurrent=false) => this.setState({ document: rotatePage(this.state.document, pageIdx, rotation, ignoreCurrent) })}
                submit={this.submitFillable}

                // Authentication
                auth={this.state.access.password ? ('&authEmail=' + this.state.access.email + '&authPass=' + this.state.access.password) : (() => {
                    const query = new URLSearchParams(this.props.location.search);
                    const email = query.get('email');
                    const fill = query.get('fill');
                    return email ? "&authEmail=" + email + "&fill=" + fill : "";
                })()}
                accessHandler={this.handleAccessCheck}
            />

            {/* Password required? */}
            {this.state.access.required && <PublicPassword
                modal={() => this.setState({ access: {
                    ...this.state.access,
                    required: false
                } })}
                data={(() => {
                    const query = new URLSearchParams(this.props.location.search);
                    const email = query.get('email');
                    if (email) return email;
                })()}
                setCredentials={async (email, password) => {
                    await this.props.loading(1005);
                    
                    // Validate credentials
                    const res = await api.post('/document/public/validate', {
                        docId: this.props.match.params.documentId,
                        authEmail: email,
                        authPass: password
                    });

                    if (res && res.success)
                        await this.setState({
                            access: {
                                required: false,
                                email,
                                password
                            }
                        });

                    this.props.loaded();
                }}
            />}

            {/* Modals */}
            {this.state.modal === "collect-signature" ?
            <PublicSignature
                modal={this.modal}
                data={this.state.modalData}
            /> : ""}

        </>);
    }
}

export default withRouter(Public);